import { KEYS } from 'platejs';

const TABLE_KEY = (KEYS as any).table as string; // 'table'
const HR_KEY = (KEYS as any).hr as string; // 'hr'
const P_KEY = (KEYS as any).p as string; // 'p'

// Top-level block voids that have no caret target inside them. A caret can
// never land between two of these, nor below a trailing one, without an
// editable block wedged in. Scoped to table + hr per the cursor-placement fix
// (the cases users hit: two stacked tables, an interior divider).
const NON_CARET_VOID_TYPES = new Set([TABLE_KEY, HR_KEY]);

const isNonCaretVoid = (node: any): boolean => Boolean(node?.type && NON_CARET_VOID_TYPES.has(node.type));

const isParagraph = (node: any): boolean => node?.type === P_KEY;

export const emptyParagraph = () => ({ type: P_KEY, children: [{ text: '' }] });

/**
 * Decide whether a single empty paragraph must be inserted into the ROOT
 * children to keep a caret target between/around block voids, and where.
 *
 * Returns the insertion index (the position the new paragraph should take in
 * `root`) or `null` when nothing is needed. Exactly ONE insertion is returned
 * per call so the caller can insert + return immediately and let Slate re-run
 * normalization for any remaining gaps (loop-safe, idempotent).
 *
 * Scope is strictly INTERIOR adjacency:
 *   • Between two adjacent top-level voids (table↔table, hr↔table,
 *     table↔hr, hr↔hr) → insert between them.
 *   • After an INTERIOR hr (one that is followed by another node) when the
 *     next node is a void (no caret target) → insert after the hr.
 *
 * The document TAIL is intentionally left to TrailingBlockPlugin — we never
 * act on the last node, so we can't fight it into an insert loop. The insert
 * is only ever an empty paragraph BETWEEN two existing nodes, and the new
 * paragraph is editable, so the same gap can't re-fire on the next pass.
 */
export function findInterVoidInsertIndex(root: any[]): number | null {
    if (!Array.isArray(root) || root.length < 2) return null;

    for (let i = 0; i < root.length - 1; i++) {
        const node = root[i];
        const next = root[i + 1];

        // Already separated by an editable paragraph — nothing to do here.
        if (isParagraph(node) || isParagraph(next)) continue;

        // Two adjacent voids (table/hr × table/hr), OR an interior hr followed
        // by any void: both leave no caret target between the pair. Wedge one
        // empty paragraph between them.
        if (isNonCaretVoid(node) && isNonCaretVoid(next)) {
            return i + 1;
        }
    }

    return null;
}
